import { useCreateWorkOrder } from '@/api/equipment/work-orders/createWorkOrder';
import { useDeleteTask } from '@/api/system/tasks/deleteTask';
import { useUpdateTask } from '@/api/system/tasks/updateTask';
import { ConfirmationDialog } from '@/components/Elements';
import { FormInput, FormSelect, FormTextarea } from '@/components/Form';
import { Button } from '@/components/ui/elements/button';
import { Form } from '@/components/ui/form/form';
import { PolicyGate } from '@/features/auth/authorization';
import { LocationContext } from '@/providers/location';
import { WorkOrder } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useMaintainables } from '../../hooks/useMaintainables';

type WorkOrderFormProps = {
  workOrder?: WorkOrder;
  onCreate?: (data: WorkOrder) => void;
  onUpdate?: (data: WorkOrder) => void;
  onDelete?: () => void;
  allowDelete?: boolean;
};

const schema = z.object({
  name: z.string().min(1, 'Required'),
  description: z.string().nullish(),
  taskable: z.string(),
});

export const WorkOrderForm = ({
  workOrder,
  onCreate,
  onUpdate,
  onDelete,
  allowDelete = false,
}: WorkOrderFormProps) => {
  const { activeLocationId } = useContext(LocationContext);
  const { equipmentOptions } = useMaintainables({ activeLocationId });

  const { mutate: createWorkOrder } = useCreateWorkOrder({
    onSuccess: onCreate,
  });
  const { mutate: updateWorkOrder } = useUpdateTask({
    onSuccess: onUpdate,
  });
  const { mutate: deleteWorkOrder } = useDeleteTask({
    onSuccess: onDelete,
  });

  const handleSubmit = (values: z.infer<typeof schema>) => {
    if (workOrder) {
      handleUpdate(values);
    } else {
      handleCreate(values);
    }
  };

  const handleCreate = (values: z.infer<typeof schema>) => {
    const vales = values.taskable.split(':');
    const taskableId = vales[1];
    const taskableType = vales[0];

    createWorkOrder({
      data: {
        ...values,
        franchise_id: activeLocationId,
        taskable: undefined,
        taskable_id: parseInt(taskableId),
        taskable_type: taskableType,
      },
    });
  };

  const handleUpdate = (values: z.infer<typeof schema>) => {
    if (workOrder)
      updateWorkOrder({
        data: {
          ...values,
        },
        id: workOrder.id,
      });
  };

  const handleDelete = () => {
    if (workOrder) deleteWorkOrder({ id: workOrder.id });
  };

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
        <div className="flex flex-row space-x-2">
          <FormInput control={form.control} name="name" label="What is the problem?" />
        </div>
        <FormTextarea control={form.control} name="description" label="Any additional details?" />
        <div className="flex space-x-2">
          {equipmentOptions && (
            <FormSelect
              control={form.control}
              name="taskable"
              label="Equipment Affected"
              options={equipmentOptions}
              placeholder="Choose a vehicle..."
            />
          )}
        </div>

        <div className="flex justify-between">
          <PolicyGate policy="equipment.work-orders.delete" meta={{ workOrder }}>
            {workOrder && allowDelete && (
              <ConfirmationDialog
                triggerButton={<Button variant="destructive">Delete</Button>}
                confirmButtonText="Delete"
                confirmAction={handleDelete}
                title={`Delete work order called ${workOrder.name}?`}
                body="Are you sure you want to delete this work order? This action cannot be undone."
              />
            )}
          </PolicyGate>
          <Button
            type="submit"
            className="w-full md:w-auto mt-12 py-8 text-xl md:text-base md:mt-0 md:py-2"
          >
            Submit
          </Button>
        </div>
      </form>
    </Form>
  );
};
